import { translate } from '@jsverse/transloco';
import * as Highcharts from 'highcharts';

import { Utils } from 'app/utils/utils';
import { AbstractFeatureChartReport } from '../chart-report-abstract/chart-report-abstract';
import { ChartDataService } from '../../chart-data.service';

export class FeatureSentimentScoreOverTimeChartReport extends AbstractFeatureChartReport {
  reportName = 'featureSentimentScoreOverTime';
  reportHandler = ChartDataService.forDateHistogram;

  getFields() {
    return super.getFields().concat(['dateInterval']);
  }

  getAggregators() {
    return ['AVG', 'SUM', 'total_AVG', 'total_SUM'];
  }

  getGraphTypes() {
    return ['line', 'column', 'spline'];
  }

  isAnalyticItemSupported(_analyticItem) {
    return true;
  }

  public canSetDateMarkers(): boolean {
    return true;
  }

  public applyComparisonToSeries(_comparison: any, _series: any, _seriesIdx: any): void {
    return;
  }

  async processResponse(response, _filterQuery?: string) {
    const sentimentItems = response.items;

    const result = { items: {} };

    for (const date of Object.getOwnPropertyNames(sentimentItems)) {
      result.items[date] = {};
      if (sentimentItems[date]['count']) {
        for (const featureName of Object.getOwnPropertyNames(sentimentItems[date]['count'])) {
          result.items[date][featureName] = {
            sentimentScore: sentimentItems[date]['count'][featureName],
            documentCount: sentimentItems[date]['docCount'][featureName],
            pointId: featureName,
          };
        }
      }
    }
    result['extra'] = response['extra'] ? response['extra'] : {};
    result['reportName'] = response['reportName'];
    return result;
  }

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);

    const aggregatorSuffix = widget.payload.aggregator.includes('AVG') ? 'Avg' : 'Sum';

    ret.tooltip.pointFormatter = function () {
      return `${translate('widgets.charting.tooltip.name-part', {
        name: this.series.name,
      })} <span style="color: ${
        this.color
      }">${FeatureSentimentScoreOverTimeChartReport.getSeriesSymbolChar(
        this.series.symbol
      )}</span><br/>
       ${translate('widgets.charting.tooltip.sentiment-aggregator-part', {
         aggregatorSuffix,
         sentiment: Utils.round(this.y, this.series.options.maxDecimals),
       })}<br/>
       ${translate('widgets.charting.tooltip.count-part', {
         count: Utils.addThousandSeparator(this.documentCount),
       })}`;
    };

    return ret;
  }
}

export class MetadataSentimentScoreOverTimeChartReport extends FeatureSentimentScoreOverTimeChartReport {
  reportName = 'metadataSentimentScoreOverTime';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    const formatterFn = this.formatMetadataValueFactory;

    ret.series.forEach((s) => {
      s.custom = {
        seriesId: s.name,
      };
      s.name = formatterFn(s.name);
    });

    return ret;
  }
}

export class TaxonomySentimentScoreOverTimeChartReport extends FeatureSentimentScoreOverTimeChartReport {
  reportName = 'taxonomySentimentScoreOverTime';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getTaxonomyHighChartsOptions(dashboard, widget, response, options);

    ret.series.forEach((serie) => {
      serie.name = serie.data[0]?.pointDisplay || serie.name;
      serie.custom = {
        seriesId: serie.data[0].pointId,
      };
    });

    const aggregatorSuffix = widget.payload.aggregator.includes('AVG') ? 'Avg' : 'Sum';
    const taxonomyNodeFromIdFn = super.getTaxonomyNodeFromIdFn(ret);

    ret.tooltip = ret.tooltip || {};
    ret.tooltip.formatter = function () {
      const index = this.series.data.indexOf(this.point);
      const id = this.series.data[index].pointId;
      const taxonomyNode = taxonomyNodeFromIdFn(id);

      const dateTextPart =
        widget.payload.dateInterval === 'week'
          ? 'widgets.charting.tooltip.date-part-week-bold'
          : 'widgets.charting.tooltip.date-part';

      const datePrefix = `${translate(dateTextPart, {
        date: Highcharts.dateFormat(this.DEFAULT_DATE_FORMAT, +this.x),
      })}<br/>`;
      const nameSuffix = `<span style="color: ${
        this.color
      }">${TaxonomySentimentScoreOverTimeChartReport.getSeriesSymbolChar(
        this.series.symbol
      )}</span>`;

      const docCount = this.series.data[index]['documentCount'] || 0;

      return (
        datePrefix +
        `${translate('widgets.charting.tooltip.name-part', {
          name: taxonomyNode.name + ' ' + nameSuffix,
        })}<br>
          ${translate('widgets.charting.tooltip.path-part', { path: taxonomyNode.path })}<br>
          ${translate('widgets.charting.tooltip.sentiment-aggregator-part', {
            aggregatorSuffix,
            sentiment: Utils.addThousandSeparator(this.y),
          })}<br>
          ${translate('widgets.charting.tooltip.count-part', { count: docCount })}`
      );
    };

    return ret;
  }
}

export class FeatureSentimentScoreChartReport extends AbstractFeatureChartReport {
  reportName = 'featureSentimentScore';
  reportHandler = ChartDataService.forDocumentCount;

  getAggregators() {
    return ['AVG', 'SUM', 'total_AVG', 'total_SUM'];
  }

  getGraphTypes() {
    return ['bar', 'column'];
  }

  isAnalyticItemSupported(analyticItem) {
    return ['documents'].indexOf(analyticItem) < 0;
  }

  canColorByPoint(): boolean {
    return true;
  }

  async processResponse(response) {
    if (this.reportName === 'taxonomySentimentScore') {
      return response;
    }
    const sentimentItems = response.items.count;
    const documentItems = response.items.docCount;

    const result = { items: { count: {} } };
    if (!sentimentItems) {
      return result;
    }

    for (const featureName of Object.getOwnPropertyNames(sentimentItems)) {
      result.items.count[featureName] = {
        sentimentScore: sentimentItems[featureName],
        documentCount: documentItems[featureName],
      };
    }

    return result;
  }
}

export class MetadataSentimentScoreChartReport extends FeatureSentimentScoreChartReport {
  reportName = 'metadataSentimentScore';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    const formatterFn = this.formatMetadataValueFactory;

    ret.xAxis.labels = ret.xAxis.labels || {};
    ret.xAxis.labels.formatter = function () {
      return formatterFn(this.value);
    };

    const aggregatorSuffix = widget.payload.aggregator.includes('AVG') ? 'Avg' : 'Sum';
    ret.tooltip.formatter = function () {
      let tooltip = `${translate('widgets.charting.tooltip.name-part', {
        name: formatterFn(this.point.pointId),
      })}<br>
      ${translate('widgets.charting.tooltip.sentiment-aggregator-part', {
        aggregatorSuffix,
        sentiment: this.y.toFixed(1),
      })}<br/>`;

      if (typeof this.point.documentCount !== 'undefined') {
        tooltip += `${translate('widgets.charting.tooltip.count-part', {
          count: Utils.addThousandSeparator(this.point.documentCount),
        })}`;
      }

      return tooltip;
    };

    return ret;
  }
}

export class TaxonomySentimentScoreChartReport extends FeatureSentimentScoreChartReport {
  reportName = 'taxonomySentimentScore';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getTaxonomyHighChartsOptions(dashboard, widget, response, options);

    const aggregatorSuffix = widget.payload.aggregator.includes('AVG') ? 'Avg' : 'Sum';
    const taxonomyNodeFromIdFn = super.getTaxonomyNodeFromIdFn(ret);

    ret.tooltip.formatter = function () {
      const id = this.point.pointId || this.key;
      const taxonomyNode = taxonomyNodeFromIdFn(id);

      const sentimentDocsLookup = response.items['docCount'];
      const docCount = sentimentDocsLookup[id] ? sentimentDocsLookup[id] : 0;
      return `${translate('widgets.charting.tooltip.name-part', { name: taxonomyNode.name })}<br>
      ${translate('widgets.charting.tooltip.path-part', { path: taxonomyNode.path })}<br>
      ${translate('widgets.charting.tooltip.sentiment-aggregator-part', {
        aggregatorSuffix,
        sentiment: Utils.addThousandSeparator(this.y),
      })}<br>
      ${translate('widgets.charting.tooltip.count-part', { count: docCount })}`;
    };

    ret.xAxis.labels = ret.xAxis.labels || {};
    ret.xAxis.labels.formatter = function () {
      const labelId = this.value || this.key;
      const taxonomyNode = taxonomyNodeFromIdFn(labelId);
      return taxonomyNode?.name || labelId;
    };

    return ret;
  }
}
